import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { McIcon } from '@maersk-global/mds-react-wrapper/components-core/mc-icon';

const maerskFontFamily = '"Maersk Text", -apple-system, BlinkMacSystemFont, "Microsoft JhengHei", "Microsoft Yahei", "微软雅黑", STXihei, "华文细黑", sans-serif';

const ChangeSummaryCard = ({
  title = 'My Changes',
  subtitle = 'Past & Upcoming 30 days',
  totalChanges = 0,
  successCount = 0,
  inProgressCount = 0,
  failureCount = 0,
  overdueCount = 0,
  conflictCount = 0,
  incidentsCount = 0,
  isLoading = false,
  onOverdueClick,
  onCategoryClick,
  isActive = false,
  isInteractive = false,
  onCardClick,
  onConflictsClick,
  onIncidentsClick,
  showStatusPills = true
}) => {
  const breakdownItems = [
    { label: 'Successful', value: successCount, type: 'success', color: '#0f9d58' },
    { label: 'In Progress', value: inProgressCount, type: 'in progress', color: '#ca8a04' },
    { label: 'Failed', value: failureCount, type: 'failure', color: '#dc2626' }
  ];

  const statusPills = [
    {
      key: 'conflicts',
      label: conflictCount === 1 ? '1 Conflict' : `${conflictCount} Conflicts`,
      background: '#ede9fe',
      color: '#6d28d9',
      count: conflictCount,
      onClick: onConflictsClick
    },
    {
      key: 'incidents',
      label: incidentsCount === 1 ? '1 Incident' : `${incidentsCount} Incidents`,
      background: '#fee2e2',
      color: '#b91c1c',
      count: incidentsCount,
      onClick: onIncidentsClick
    }
  ];

  const renderContent = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 3, mb: '2px' }}>
        {/* Left block: Header + Big number */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                background: '#dbeafe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <McIcon icon="clipboard-list-text" size="24" color="#4b5563" />
            </Box>
            <Box sx={{ fontFamily: maerskFontFamily, textAlign: 'left' }}>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, letterSpacing: '0.08em', color: '#4b5563', fontFamily: maerskFontFamily }}>
                {title}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', fontFamily: maerskFontFamily }}>{subtitle}</Typography>
            </Box>
          </Box>
          <Typography sx={{ fontSize: '34px', fontWeight: 600, color: '#111827', lineHeight: 1, fontFamily: maerskFontFamily }}>
            {totalChanges}
          </Typography>
        </Box>

        {/* Right block: Overdue, Conflicts, Incidents */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: showStatusPills ? 1 : 0 }}>
          <Box
            role="button"
            tabIndex={0}
            onClick={() => onOverdueClick && overdueCount > 0 && onOverdueClick('overdue')}
            onKeyPress={(event) => {
              if (event.key === 'Enter' && onOverdueClick && overdueCount > 0) {
                onOverdueClick('overdue');
              }
            }}
            sx={{
              backgroundColor: '#fef3c7',
              color: '#92400e',
              borderRadius: '999px',
              padding: '4px 10px',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontWeight: 700,
              cursor: overdueCount > 0 ? 'pointer' : 'not-allowed',
              opacity: overdueCount > 0 ? 1 : 0.6,
              fontFamily: maerskFontFamily,
              minWidth: 100,
              justifyContent: 'center'
            }}
          >
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, fontFamily: maerskFontFamily }}>{overdueCount}</Typography>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, fontFamily: maerskFontFamily }}>Overdue</Typography>
          </Box>

          {showStatusPills && statusPills.map((pill) => {
            const isDisabled = !pill.onClick || (pill.count || 0) <= 0;
            return (
              <Box
                key={pill.key}
                role={isDisabled ? undefined : 'button'}
                tabIndex={isDisabled ? -1 : 0}
                onClick={() => {
                  if (!isDisabled) {
                    pill.onClick();
                  }
                }}
                onKeyPress={(event) => {
                  if (!isDisabled && event.key === 'Enter') {
                    pill.onClick();
                  }
                }}
                sx={{
                  backgroundColor: pill.background,
                  color: pill.color,
                  borderRadius: '999px',
                  padding: '4px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontWeight: 700,
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  opacity: isDisabled ? 0.5 : 1,
                  fontFamily: maerskFontFamily,
                  fontSize: '0.75rem',
                  minWidth: 100,
                  justifyContent: 'center'
                }}
              >
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, fontFamily: maerskFontFamily }}>
                  {pill.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Typography
        sx={{
          fontSize: '1rem',
          color: '#4b5563',
          fontWeight: 500,
          fontFamily: maerskFontFamily,
          textAlign: 'left',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 0.5
        }}
      >
        {breakdownItems.map((item, index) => (
          <React.Fragment key={item.type}>
            <Box
              component="span"
              role="button"
              tabIndex={0}
              onClick={() =>
                onCategoryClick && item.value > 0 && onCategoryClick(item.type)
              }
              onKeyPress={(event) => {
                if (event.key === 'Enter' && onCategoryClick && item.value > 0) {
                  onCategoryClick(item.type);
                }
              }}
              sx={{
                display: 'inline-flex',
                alignItems: 'baseline',
                gap: 0.5,
                cursor: item.value > 0 ? 'pointer' : 'not-allowed',
                opacity: item.value > 0 ? 1 : 0.6
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: '1.05rem',
                  fontWeight: 700,
                  color: item.color,
                  fontFamily: maerskFontFamily
                }}
              >
                {item.value}
              </Typography>
              <Typography component="span" sx={{ fontSize: '0.95rem', color: '#4b5563', fontFamily: maerskFontFamily }}>
                {item.label}
              </Typography>
            </Box>
            {index < breakdownItems.length - 1 && (
              <Typography component="span" sx={{ color: '#9ca3af', fontFamily: maerskFontFamily }}>
                /
              </Typography>
            )}
          </React.Fragment>
        ))}
      </Typography>
    </Box>
  );

  const handleCardActivation = (event) => {
    if (!isInteractive || !onCardClick) return;
    if (event?.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    if (event?.type === 'keydown') {
      event.preventDefault();
    }
    onCardClick();
  };

  return (
    <Box
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={handleCardActivation}
      onKeyDown={handleCardActivation}
      sx={{
        width: '100%',
        backgroundColor: '#fff',
        borderRadius: 2,
        padding: '20px',
        border: isActive ? '2px solid #42b0d5' : '1px solid #9ca3af',
        minHeight: 180,
        fontFamily: maerskFontFamily,
        position: 'relative',
        cursor: isInteractive ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexGrow: 1 }}>
          <CircularProgress />
        </Box>
      ) : (
        renderContent()
      )}
    </Box>
  );
};

export default ChangeSummaryCard;
